import React from 'react';
import { CheckIcon, NoSymbolIcon, TrashIcon, ArrowUturnLeftIcon, DocumentPlusIcon, PaperClipIcon, PlusCircleIcon } from '@heroicons/react/24/outline';

/**
 * Per-record Accept / Reject / Delete (and Clear) buttons for a Pending EDI Order, shared
 * by the card view and the table's Actions column. Which buttons show depends on the row's
 * acceptreject state (Lee 2026-08-06):
 *   blank  -> Accept, Reject, Delete   (accept/reject only allowed when blank)
 *   'A'    -> Clear, Delete            (undo the accept, or delete; can't re-accept/reject)
 * Rejected ('R') / deleted rows never appear on the board, so only these two states occur.
 *
 * Handlers are pre-bound to this row by the caller. `compact` (table) drops the labels to
 * save width; the card shows icon + label.
 */
interface Props {
  acceptreject: string;
  onAccept?: () => void;
  onReject?: () => void;
  onDelete?: () => void;
  onClear?: () => void;
  /** Get Template — shown only when eligible (blank template + mapped delivery). */
  onGetTemplate?: () => void;
  canGetTemplate?: boolean;
  /** Attach to an existing pro — shown only when eligible (accepted + no missing product). */
  onAttach?: () => void;
  canAttach?: boolean;
  /** Create a NEW pro from this EDI order — shown only when eligible (accepted + valid
   *  template + no missing product). */
  onCreate?: () => void;
  canCreate?: boolean;
  compact?: boolean;
  /** Stack the buttons vertically (card view) instead of in a row (table). */
  vertical?: boolean;
  busy?: boolean;
}

const EdiRowActions: React.FC<Props> = ({ acceptreject, onAccept, onReject, onDelete, onClear, onGetTemplate, canGetTemplate, onAttach, canAttach, onCreate, canCreate, compact, vertical, busy }) => {
  const accepted = (acceptreject || '') === 'A';

  const btn = (
    key: string,
    label: string,
    icon: React.ReactNode,
    onClick: (() => void) | undefined,
    cls: string,
  ) =>
    onClick ? (
      <button
        key={key}
        type="button"
        disabled={busy}
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        title={label}
        aria-label={label}
        className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[0.6875rem] font-medium transition-colors disabled:opacity-50 ${vertical ? 'w-full justify-center' : ''} ${cls}`}
      >
        {icon}
        {!compact && <span>{label}</span>}
      </button>
    ) : null;

  return (
    <div className={vertical ? 'flex flex-col items-stretch gap-1.5' : 'inline-flex items-center gap-1.5'} onClick={(e) => e.stopPropagation()}>
      {canCreate && onCreate && btn('create', 'Create', <PlusCircleIcon className="w-3.5 h-3.5" />, onCreate,
        'border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-400 dark:hover:bg-emerald-900/30')}
      {canAttach && onAttach && btn('attach', 'Attach', <PaperClipIcon className="w-3.5 h-3.5" />, onAttach,
        'border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/30')}
      {canGetTemplate && onGetTemplate && btn('gettemplate', 'Get Template', <DocumentPlusIcon className="w-3.5 h-3.5" />, onGetTemplate,
        'border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-400 dark:hover:bg-indigo-900/30')}
      {!accepted && btn('accept', 'Accept', <CheckIcon className="w-3.5 h-3.5" />, onAccept,
        'border-green-300 text-green-700 hover:bg-green-50 dark:border-green-700 dark:text-green-400 dark:hover:bg-green-900/30')}
      {!accepted && btn('reject', 'Reject', <NoSymbolIcon className="w-3.5 h-3.5" />, onReject,
        'border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-900/30')}
      {accepted && btn('clear', 'Clear', <ArrowUturnLeftIcon className="w-3.5 h-3.5" />, onClear,
        'border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700')}
      {btn('delete', 'Delete', <TrashIcon className="w-3.5 h-3.5" />, onDelete,
        'border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/30')}
    </div>
  );
};

export default EdiRowActions;
